/**
 * Reports API
 * Report generation and export
 */

import apiClient from '@/lib/api-client'
import type {
  ReportTypeInfo,
  ReportGenerateRequest,
  ReportResponse,
} from '@/types/report'

export const reportsApi = {
  /**
   * Get available report types
   */
  async getTypes(): Promise<ReportTypeInfo[]> {
    const response = await apiClient.get<ReportTypeInfo[]>('/reports/types')
    return response.data
  },

  /**
   * Generate report with pagination
   */
  async generate(request: ReportGenerateRequest): Promise<ReportResponse> {
    const response = await apiClient.post<ReportResponse>(
      '/reports/generate',
      request
    )
    return response.data
  },

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<{
    total_clients: number
    total_visits: number
    total_cases: number
    active_cases: number
    visits_this_month: number
    clients_this_month: number
    pending_visits: number
  }> {
    const response = await apiClient.get('/reports/dashboard/stats')
    return response.data
  },

  /**
   * Get dashboard charts data
   */
  async getDashboardCharts(): Promise<{
    cases_by_type: Array<{ label: string; value: number; percentage?: number }>
    visits_by_month: Array<{ label: string; value: number }>
    top_assistance_types: Array<{ label: string; value: number }>
    visits_by_status: Array<{ label: string; value: number; percentage?: number }>
  }> {
    const response = await apiClient.get('/reports/dashboard/charts')
    return response.data
  },

  /**
   * Export report to Excel
   * Returns a blob for download
   */
  async exportToExcel(request: ReportGenerateRequest): Promise<Blob> {
    const response = await apiClient.post<Blob>(
      '/reports/export/excel',
      request,
      {
        responseType: 'blob',
      }
    )
    return response.data
  },

  /**
   * Export all report types into a single Excel file (one sheet per report)
   */
  async exportCombinedToExcel(request: {
    date_from: string
    date_to: string
    report_types?: string[]
  }): Promise<Blob> {
    const response = await apiClient.post<Blob>(
      '/reports/export/excel/combined',
      request,
      {
        responseType: 'blob',
      }
    )
    return response.data
  },
}

export default reportsApi
